import { Link } from "react-router-dom";
import { Heart, MapPin, Eye } from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import { fileUrl } from "@/lib/api";
import { formatPrice } from "@/lib/constants";
import { typeLabel } from "@/lib/i18n";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function PropertyCard({ property, onFavChange, listQuery }) {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();
  const [fav, setFav] = useState(!!property.is_favorite);

  const cover = property.images?.[property.cover_index || 0] || property.images?.[0];
  const coverUrl = cover ? fileUrl(cover) : "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=800&q=80";

  const toggleFav = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { toast.info(t.auth.loginNeeded); nav("/login"); return; }
    try {
      if (fav) { await api.delete(`/favorites/${property.id}`); setFav(false); }
      else { await api.post(`/favorites/${property.id}`); setFav(true); }
      onFavChange?.(property.id, !fav);
    } catch {}
  };

  const from = listQuery ? `/properties?${listQuery}` : null;

  return (
    <Link
      to={`/property/${property.id}`}
      state={from ? { from } : undefined}
      data-testid={`property-card-${property.id}`}
      className="fl-card group relative bg-white rounded-2xl overflow-hidden border border-slate-200/80 flex flex-col"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img src={coverUrl} alt={property.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        <div className="absolute top-3 start-3">
          <Badge className="bg-emerald-900 text-white hover:bg-emerald-900">{typeLabel(property.type, lang)}</Badge>
        </div>
        {property.featured && (
          <div className="absolute top-3 end-14">
            <Badge className="bg-amber-500 text-white hover:bg-amber-500">{lang === "ar" ? "مميز" : "Featured"}</Badge>
          </div>
        )}
        <button data-testid={`fav-btn-${property.id}`} onClick={toggleFav} aria-label="favorite"
          className={`absolute top-3 end-3 w-9 h-9 rounded-full grid place-items-center backdrop-blur bg-white/90 hover:bg-white transition ${fav ? "text-rose-600" : "text-slate-600"}`}>
          <Heart className="w-5 h-5" fill={fav ? "currentColor" : "none"} />
        </button>
      </div>
      <div className="p-4 flex flex-col gap-2 flex-1">
        <h3 className="text-[15px] sm:text-base font-bold text-emerald-950 line-clamp-2 leading-snug">{property.title}</h3>
        <div className="flex items-center gap-1 text-xs text-slate-500">
          <MapPin className="w-3.5 h-3.5" />
          <span>{property.governorate}{property.city ? ` · ${property.city}` : ""}{property.area ? ` · ${property.area}` : ""}</span>
        </div>
        {property.details?.area_sqm ? (
          <div className="text-xs text-slate-600">{formatPrice(property.details.area_sqm)} {t.common.sqm}</div>
        ) : null}
        <div className="mt-auto pt-2 flex items-center justify-between">
          <div className="fl-num text-lg font-extrabold text-emerald-950">
            {formatPrice(property.price)} <span className="text-xs text-slate-500 font-normal">{t.common.currency}</span>
          </div>
          <div className="flex items-center gap-1 text-xs text-slate-400">
            <Eye className="w-3.5 h-3.5" /> {property.views || 0}
          </div>
        </div>
      </div>
    </Link>
  );
}
